import React from "react"
import { SUBJECT_META } from "../learnerReportModel"
import { EmptyNote, PillGroup, SUBJECT_FILTERS } from "../reportUi"
import type { SubjectFilter } from "../reportUi"
import type { ReportView } from "./reportView"

function mapColors(score: number | null): [string, string, string] {
  if (score === null) return ["#C3C8D4", "#F6F7FB", "#E3E6EE"]
  if (score >= 9) return ["#2E9D5E", "#EEF8F2", "#BFE3CC"]
  if (score >= 7) return ["#3B5BCC", "#EEF1FB", "#C7D1F2"]
  if (score >= 4) return ["#E0A040", "#FFF8EC", "#F3D9AE"]
  return ["#D7354B", "#FDF0F2", "#F4C1C9"]
}

const LEGEND: Array<[string, string]> = [
  ["Passed", "#2E9D5E"],
  ["Good", "#3B5BCC"],
  ["In progress", "#E0A040"],
  ["Weak", "#D7354B"],
  ["Not started", "#C3C8D4"],
]

const Line = ({ w, color = "#CBD1DD" }: { w: number; color?: string }) => (
  <div style={{ flex: "none", width: w, height: 2, background: color }} />
)

const SkillMapTab: React.FC<{ view: ReportView }> = ({ view }) => {
  const [subject, setSubject] = React.useState<SubjectFilter>("both")
  const [showReco, setShowReco] = React.useState(true)

  const tree = view.subjects
    .filter(s => subject === "both" || s === subject)
    .map(s => {
      const sk = view.skills[s]
      return {
        subject: s,
        strands: sk.strands,
        count: sk.skills.length,
        recommended: sk.skills.filter(k => k.next).length,
      }
    })
    .filter(t => t.count > 0)

  const years = view.subjects
    .map(s => view.skills[s].currentYear)
    .filter((y): y is number => y !== null)
  const yearLabel = years.length ? `Year ${Math.max(...years)}` : ""

  return (
    <>
      <section aria-label="Map controls" className="sr-card sr-map-controls">
        <div className="sr-map-controls-left">
          <PillGroup options={SUBJECT_FILTERS} value={subject} onChange={setSubject} label="Subject" />
          <label className="sr-check sr-no-print">
            <input type="checkbox" checked={showReco} onChange={() => setShowReco(v => !v)} />
            Show recommendations
          </label>
        </div>
        <div className="sr-legend-row" style={{ gap: 14 }}>
          {LEGEND.map(([label, color]) => (
            <span key={label} className="sr-legend-item">
              <span className="sr-legend-swatch" style={{ background: color, borderRadius: "50%", width: 10, height: 10 }} />
              {label}
            </span>
          ))}
        </div>
      </section>

      <section aria-label="Skill map" className="sr-card" style={{ padding: 28, overflowX: "auto" }}>
        {tree.length === 0 ? (
          <EmptyNote>No skills to map yet.</EmptyNote>
        ) : (
          <div style={{ minWidth: 1040, display: "flex", alignItems: "center" }}>
            <div className="sr-map-student">
              <span className="sr-map-avatar">{view.initialsText}</span>
              <span style={{ fontSize: 15, fontWeight: 700 }}>{view.studentName}</span>
              {yearLabel ? <span style={{ fontSize: 12, color: "#C9D0E6" }}>{yearLabel}</span> : null}
            </div>
            <Line w={28} />
            <div className="sr-map-branch" style={{ gap: 28, padding: "4px 0" }}>
              {tree.map(t => (
                <div key={t.subject} style={{ display: "flex", alignItems: "center" }}>
                  <Line w={28} />
                  <div
                    className="sr-map-subject"
                    style={{ background: SUBJECT_META[t.subject].bg, color: SUBJECT_META[t.subject].fg }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{SUBJECT_META[t.subject].label}</span>
                    <span style={{ fontSize: 12, fontWeight: 500 }}>
                      {t.count} skills · {t.recommended} recommended
                    </span>
                  </div>
                  <Line w={28} />
                  <div className="sr-map-branch" style={{ gap: 14, padding: "2px 0" }}>
                    {t.strands.map(st => (
                      <div key={st.name} style={{ display: "flex", alignItems: "center" }}>
                        <Line w={24} color="#E1E5EC" />
                        <div className="sr-map-strand">{st.name}</div>
                        <Line w={24} color="#E1E5EC" />
                        <div className="sr-map-branch sr-map-branch-light" style={{ gap: 6, padding: "2px 0" }}>
                          {st.skills.map(k => {
                            const [dot, bg, border] = mapColors(k.score)
                            return (
                              <div key={k.name} style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                                <Line w={18} color="#E1E5EC" />
                                <div className="sr-map-pill" style={{ background: bg, border: `1px solid ${border}` }}>
                                  <span className="sr-dot" style={{ width: 10, height: 10, background: dot }} />
                                  <span style={{ fontSize: 13, fontWeight: 500 }}>{k.name}</span>
                                  <span className="sr-mono" style={{ fontSize: 12, fontWeight: 700, color: "#4A5163" }}>
                                    {k.score === null ? "–" : k.score.toFixed(1)}
                                  </span>
                                </div>
                                {showReco && k.next ? (
                                  <>
                                    <div style={{ flex: "none", width: 22, borderTop: "2px dashed #1F2F6B" }} />
                                    <div className="sr-map-reco">
                                      <span className="sr-map-reco-when">{k.next.when}</span>
                                      <span
                                        className="sr-map-reco-type"
                                        style={
                                          k.next.basket === "Relearn"
                                            ? { background: "#FDEEDB", color: "#8A5300" }
                                            : { background: "#E4EAFB", color: "#2E4AA6" }
                                        }
                                      >
                                        {k.next.basket}
                                      </span>
                                      <span style={{ fontSize: 12, fontWeight: 600 }}>{k.next.title}</span>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      <p className="sr-footnote">
        Each skill shows its current{yearLabel ? ` ${yearLabel}` : ""} score. Dashed links mark where the next
        recommended activities land.
      </p>
    </>
  )
}

export default SkillMapTab
